import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@material-ui/core';
import Inbox from '@material-ui/icons/MailOutline';
import { useParams } from 'react-router';
import PermittingDocumentsTable from './PermittingDocumentsTable';
import { ModalDefaultDraggable } from '../../../../molecules';
import AddPermittingDocumentPopup from './AddPermittingDocumentPopup';
import Dropdown from './Dropdown';
import { useDownloadWikiPermittingDocumentsMutation, useGetWikiPermittingDocumentQuery } from '../../../../redux/rtkQuery/apiWikiPermittingSlice';
import ShowEmailTemplatePopup from './ShowEmailTemplatePopup';

const Documents = () => {
  const [showAddDocument, setShowAddDocument] = useState<boolean>(false);
  const [showEmailTemplate, setShowEmailTemplate] = useState<boolean>(false);
  // @ts-ignore
  const { wikiId } = useParams();
  const [work, setWork] = useState<any>();
  const [building, setBuilding] = useState<any>();
  const [permitType, setPermitType] = useState<any>();
  const [params, setParams] = useState<any>({});
  const [emailActive, setEmailActive] = useState<boolean>(false);
  const skipData = () => {
    return !(building !== -1 && work !== -1 && permitType !== -1);
  };
  const { data } = useGetWikiPermittingDocumentQuery(params, { skip: skipData() });
  const [downloadWikiPermittingDocuments] = useDownloadWikiPermittingDocumentsMutation();

  useEffect(() => {
    const _params = {
      jurisdiction_id: wikiId,
      work: work ? work.value : -1,
      building: building ? building.value : -1,
      permit_type: permitType ? permitType.value : -1,
    };
    setParams(_params);
    setEmailActive(!!work && !!building && !!permitType);
  }, [work, building, permitType]);

  const createEmailTemplate = () => {
    if (!emailActive) {
      return;
    }
    setShowEmailTemplate(true);
  };

  const downloadAllDocuments = async () => {
    const payload = {
      ids: data.results.map(d => d.id),
    };
    const result = await downloadWikiPermittingDocuments(payload);
    let fileName = '';
    try {
      fileName = result.data.contentDisposition.split("filename=")[1].replace(/"/g, '');
    } catch (e) {
      fileName = 'files.zip';
    }

    const binaryData = atob(result.data.file);
    const uint8Array = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      uint8Array[i] = binaryData.charCodeAt(i);
    }

    const blob = new Blob([uint8Array], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  };

  const isDisabledDownload = () => {
    if (data && data.results && data.results.length > 0) {
      return false;
    }
    return true;
  };
  return (
    <>
      <Card>
        <HeaderRow>
          <SectionTitle>Documents</SectionTitle>
          <Actions active={emailActive}>
            <Button color="primary" variant="contained" disabled={isDisabledDownload()} onClick={downloadAllDocuments}>
              Download All
            </Button>
            <Button color="primary" variant="contained" onClick={e => setShowAddDocument(true)}>
              Add Document
            </Button>
            <EmailIconButton title="Email template" onClick={createEmailTemplate}>
              <Inbox />
            </EmailIconButton>
          </Actions>
        </HeaderRow>
        <DropdownsWrapper>
          <Dropdown label="Work" entity={2} name="work" value={work} onChange={e => setWork(e)} />
          <Dropdown label="Building" entity={1} name="building" value={building} onChange={e => setBuilding(e)} />
          <Dropdown label="Permit Type" entity={14} name="permitType" value={permitType} onChange={e => setPermitType(e)} />
        </DropdownsWrapper>
        <PermittingDocumentsTable setShowAddDocument={setShowAddDocument} records={data?.results || []} />
      </Card>
      {showAddDocument && (
        <ModalDefaultDraggable title="Add Document" onClose={() => setShowAddDocument(false)}>
          <AddPermittingDocumentPopup setShowAddDocument={setShowAddDocument} />
        </ModalDefaultDraggable>
      )}
      {showEmailTemplate && (
        <ModalDefaultDraggable title="Email Template" onClose={() => setShowEmailTemplate(false)}>
          <ShowEmailTemplatePopup params={params} setShowEmailTemplate={setShowEmailTemplate} />
        </ModalDefaultDraggable>
      )}
    </>
  );
};

export default Documents;

const Card = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  margin-bottom: 20px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

export const Actions = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;

  button {
    min-width: 140px;
    height: 40px;
    border-radius: 8px;
    font-weight: 600;
    text-transform: none;
    font-size: 0.875rem;
    letter-spacing: 0.01em;
  }
`;

const EmailIconButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--primary-600);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-300);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    font-size: 1.125rem;
  }
`;

const DropdownsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px 24px;
  margin-bottom: 20px;
`;

export const HeaderWrapper = HeaderRow;
export const TopHeader = SectionTitle;
export const AddButtonWrapper = Actions;
